import { useEffect, useRef } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

/**
 * PropertyMap — Amazon Location Service basemap (Esri tiles) via MapLibre GL.
 * v1.0 Task 9.2 / Req 5.8. Replaces the Phase 1 OpenStreetMap iframe.
 *
 * Tile auth is an Amazon Location API key (geo:GetMap* only), referer-
 * restricted to our own origins by the CDK (PriceTilt-Lambda-<env> stack).
 * The key is a public, restricted credential — safe to inline in the bundle.
 *
 * Build-time env (set in Amplify, see web/.env.example):
 *   VITE_MAP_API_KEY  — fetched post-deploy via `aws location describe-key`
 *   VITE_MAP_NAME     — from SSM /pricetilt/<env>/map-name
 *   VITE_MAP_REGION   — AWS region of the map resource (default us-east-1)
 *
 * If the key/name are unset (e.g. local dev without map env), we render a
 * graceful placeholder instead of throwing — the rest of the page is fine.
 */

const API_KEY = import.meta.env.VITE_MAP_API_KEY || "";
const MAP_NAME = import.meta.env.VITE_MAP_NAME || "";
const REGION = import.meta.env.VITE_MAP_REGION || "us-east-1";

interface Props {
  lat: number;
  lon: number;
  /** Used for the marker's accessible label / popup. */
  address?: string;
  /** Initial zoom; ~15 frames a single parcel + immediate surroundings. */
  zoom?: number;
}

function styleUrl(): string {
  return (
    `https://maps.geo.${REGION}.amazonaws.com` +
    `/maps/v0/maps/${MAP_NAME}/style-descriptor?key=${API_KEY}`
  );
}

export function PropertyMap({ lat, lon, address, zoom = 15 }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    if (!API_KEY || !MAP_NAME) return; // placeholder path handles this below
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: styleUrl(),
      center: [lon, lat],
      zoom,
      attributionControl: { compact: true },
    });
    mapRef.current = map;

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    const marker = new maplibregl.Marker({ color: "#1f6feb" }).setLngLat([lon, lat]);
    if (address) {
      marker.setPopup(new maplibregl.Popup({ offset: 24 }).setText(address));
    }
    marker.addTo(map);

    return () => {
      map.remove();
      mapRef.current = null;
    };
    // Re-init when the property changes.
  }, [lat, lon, address, zoom]);

  if (!API_KEY || !MAP_NAME) {
    return (
      <div className="property-map-placeholder" role="img" aria-label="Map unavailable">
        <span>Map unavailable</span>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="property-map"
      role="img"
      aria-label={address ? `Map of ${address}` : "Property location map"}
    />
  );
}
